import { cellArea, UNITS } from "h3-js"
import { useEffect, useMemo, useState } from "react"

import { deep_copy } from "../../../../lib/core/src/utils/deep_copy"
import { CloseIcon } from "../../../components/svgs"
import { AppState } from "../../../state/interface"
import { get_app_state } from "../../../state/store"
import { is_on_localhost } from "../../../utils/is_on_localhost"
import { to_sentence_case } from "../../../utils/string"
import { get_capacity_factor_mix } from "../../data/capacity_factor_data"
import { CapacityFactorData } from "../../data/interface"
import { POWER_TYPES } from "../../model/interface"
import { PowerPlantStatusesForH3R4 } from "../../model/v3/interface"
import pub_sub from "../../state/pub_sub"
import { COLOURS } from "../constants"
import "./CellInfo.css"


const show_H3_cell_id = false || is_on_localhost()


export function CellInfo()
{
    const h3r4_id = get_app_state(state => state.view.h3r4_cell_info_open || "")
    const set_cell_info_open = get_app_state(state => state.view.set_h3r4_cell_info_open)
    const map_capacity_factors_source = get_app_state(state => state.view.map_capacity_factors_source)
    const map_capacity_factors_aggregation = get_app_state(state => state.view.map_capacity_factors_aggregation)

    const aggregated_built_power_plants = get_app_state(state => state.simulation_state.sim_state?.built_power_plants.aggregated_by_h3r4[h3r4_id])
    const aggregated_power_plant_statuses_step1 = get_app_state(state => get_stepN_cell(h3r4_id, state, 1)?.power_plants)
    const aggregated_power_plant_statuses_step2 = get_app_state(state => get_stepN_cell(h3r4_id, state, 2)?.power_plants)
    const aggregated_power_plant_statuses_mix = get_app_state(state =>
    {
        const current_time_steps = state.simulation_state.get_current_timesteps()
        return current_time_steps?.mix
    })
    const aggregated_power_plant_statuses = useMemo(() =>
    {
        return mix_aggregate_power_plant_statuses(aggregated_power_plant_statuses_step1, aggregated_power_plant_statuses_step2, aggregated_power_plant_statuses_mix)
    }, [aggregated_power_plant_statuses_step1,
        aggregated_power_plant_statuses_step2,
        aggregated_power_plant_statuses_mix])

    const demand_GW = get_app_state(state =>
    {
        // TODO also use step2 and mix to get smoother values for power plant statuses
        const step1 = state.simulation_state.get_current_timesteps()?.step1
        const step1_cell = step1?.by_h3r4[h3r4_id]
        return step1_cell?.demand_GW ?? 0
    })
    // This does not work yet with shallow from zustand/shallow.  Not sure why not.
    // const { aggregated_power_plant_statuses, demand_GW } = get_app_state(state =>
    // {
    //     // TODO also use step2 and mix to get smoother values for power plant statuses
    //     const step1 = state.simulation_state.get_current_timesteps()?.step1
    //     const step1_cell = step1?.by_h3r4[h3r4_id]
    //     const power_plant_statuses = step1_cell?.power_plant_statuses
    //     const demand_GW = step1_cell?.demand_GW ?? 0

    //     return {
    //         aggregated_power_plant_statuses: power_plant_statuses,
    //         demand_GW,
    //     }
    // }, shallow)

    const gen_cap_els = (aggregated_built_power_plants && aggregated_power_plant_statuses) ? POWER_TYPES.map(power_type =>
    {
        return {
            power_type,
            built_power_plants: aggregated_built_power_plants[power_type],
            power_plant_statuses: aggregated_power_plant_statuses[power_type],
        }
    })
    .filter(({ built_power_plants }) => built_power_plants.capacity_MW > 0)
    .sort((a, b) => b.built_power_plants.capacity_MW - a.built_power_plants.capacity_MW)
    .map(({ power_type, built_power_plants, power_plant_statuses }) =>
    {
        return <div key={power_type}>
            {to_sentence_case(power_type)} {power_plant_statuses.generated_MW.toFixed(0)} MW / {built_power_plants.capacity_MW.toFixed(0)} MW
        </div>
    }) : []


    const all_capacity_factor_data = get_app_state(state => state.data.capacity_factor_data)
    const [capacity_factor, set_capacity_factor] = useState(0)
    useEffect(() =>
    {
        if (!h3r4_id) return
        if (!all_capacity_factor_data) return
        if (!map_capacity_factors_source) return

        const unsub_ref = { current: undefined as undefined | (() => void) }

        const show_hourly = map_capacity_factors_aggregation === "hourly"


        let selected_capacity_factor_data: CapacityFactorData | undefined = undefined
        if (map_capacity_factors_source === "wind")
        {
            selected_capacity_factor_data = show_hourly ? all_capacity_factor_data.wind : all_capacity_factor_data.annual_wind
        }
        else if (map_capacity_factors_source === "solar")
        {
            selected_capacity_factor_data = show_hourly ? all_capacity_factor_data.solar : all_capacity_factor_data.annual_solar
        }

        if (!selected_capacity_factor_data) return

        if (show_hourly)
        {
            unsub_ref.current = pub_sub.sub("simulation_datetime", payload =>
            {
                const capacity_factor = get_capacity_factor_mix(selected_capacity_factor_data, payload.datetime_annual_hourly_indices_mix, h3r4_id) ?? 0
                set_capacity_factor(capacity_factor)
            })
        }
        else
        {
            const capacity_factor = get_capacity_factor_mix(selected_capacity_factor_data, { one: 1, two: 1, mix: 1 }, h3r4_id) ?? 0
            set_capacity_factor(capacity_factor)
        }

        return () =>
        {
            if (unsub_ref.current) unsub_ref.current()
        }
    }, [all_capacity_factor_data, map_capacity_factors_source, h3r4_id])


    return <div
        id="tile_info_panel"
        className={"ui_info_box " + (h3r4_id ? "open" : "closed")}
    >
        <div className="ui_info_box_header" style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
                {/* Info */}
            </div>
            <CloseIcon
                style={{ height: 24, margin: "-5px -5px 0 0" }}
                on_click={() => set_cell_info_open(undefined)}
            />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
            {demand_GW > 0 &&
            <div><b>Demand</b> {demand_GW.toFixed(2)} GW</div>}

            {gen_cap_els.length === 0 && <div><b>No power plants</b></div>}

            {gen_cap_els.length > 0 && <>
                <div><b>Generation by</b></div>
                {gen_cap_els}
            </>}

            {map_capacity_factors_source && <>
                <div><b>{to_sentence_case(map_capacity_factors_source + " capacity factor")}</b></div>
                <div>{Math.round(capacity_factor * 100).toFixed(0)}%</div>
            </>}

            {show_H3_cell_id && <span style={{
                fontSize: "var(--font-small)",
                color: COLOURS.text_muted,
                display: "flex",
                gap: 4,
            }}>
                {h3r4_id
                    ? <>
                        <div>
                            H3 cell ID: <span style={{ userSelect: "text" }}>{h3r4_id}</span>
                        </div>
                        <div>
                            Area: <span style={{ userSelect: "text" }}>{cellArea(h3r4_id, UNITS.km2).toFixed(0)} km²</span>
                        </div>
                    </>
                    : ""
                }
            </span>}
        </div>
    </div>
}

function get_stepN_cell(h3r4_id: string, state: AppState, step_num: 1 | 2)
{
    const current_time_steps = state.simulation_state.get_current_timesteps()
    const step = step_num === 1 ? current_time_steps?.step1 : current_time_steps?.step2
    return step?.by_h3r4[h3r4_id]
}


// function CellInfoText({ hovered_tile }: { hovered_tile: CellDataV1 | null })
// {
//     if (!hovered_tile) return <div>
//         {is_touch_screen() ? "Select" : "Hover over"} a tile<br/>
//         to see details
//     </div>

//     const { subtype, has_oil_rig } = hovered_tile
//     const info = land_or_sea_types[subtype]

//     return <div>
//         <div><b>Type:</b> {info.human_readable}</div>
//         {/* <div><b>Wind Turbine:</b> {hovered_tile.has_wind_turbine ? "Yes" : "No"}</div>
//         <div><b>Solar Farm:</b> {hovered_tile.has_solar_farm ? "Yes" : "No"}</div> */}
//         {has_oil_rig && <div><b>Oil Rig:</b> {has_oil_rig.state === "extracting" ? "Extracting" : "Dormant"}</div>}
//     </div>
// }


function mix_aggregate_power_plant_statuses(step1?: PowerPlantStatusesForH3R4, step2?: PowerPlantStatusesForH3R4, mix?: number): PowerPlantStatusesForH3R4 | undefined
{
    if (!step2) return step1
    if (!step1 || mix === undefined) return undefined

    const mixed = deep_copy(step1)

    return mixed
}
